import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isUndefined } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../hooks/react-redux';
import { useWindowResize } from '../../hooks/useWindowResize';
import { getHomeScreenModules, getInternetGatewayOpen, getUser, HomePageModule } from '../auth/login/store';
import { resetDatasetInstance } from '../datasets/instances/store';
import { getDocumentDetailsModalOpen, resetDocumentInstance } from '../documents/my-documents/store';
import { DocumentDetailsModal } from '../documents/my-documents/upload/DocumentDetailsModal';
import { UploadDocumentsModal } from '../documents/my-documents/upload/UploadDocumentsModal';
import { resetOpinionInstance } from '../opinions/instances/store';
import { OpinionsView, resetAllOpinions } from '../opinions/my-opinions/store';
import { PageLoadingDots } from '../shared/loading-dots/PageLoadingDots';
import styles from './Home.module.scss';
import { DocumentsModule } from './modules/documents/DocumentsModule';
import { OpinionsModule } from './modules/opinions/OpinionsModule';
import { AdminModule } from './modules/admin/AdminModule';
import { HomePageLink, systemMaintenanceOptions } from '../admin/system-maintenance/store';

const MODULE_MARGIN = 10;
const MODULE_BORDER_WIDTH = 2;
const WRAPPER_MARGIN_Y = 10;
const WRAPPER_MARGIN_X = 20;
const NAV_BAR = 100;
const MODULE_MARGIN_PADDING = 30;

export const Home: React.FC = () => {
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const [tileDimensions, setTileDimensions] = useState<undefined | { height: number; width: number; }>(undefined);

    const dispatch = useAppDispatch();
    const documentDetailsModalOpen = useAppSelector(getDocumentDetailsModalOpen);
    const homeScreenModules = useAppSelector(getHomeScreenModules);
    const internetGatewayOpen = useAppSelector(getInternetGatewayOpen);
    const user = useAppSelector(getUser);
    const [screenWidth, screenHeight, zoomRatio] = useWindowResize();

    const widthDivider = useMemo(() => homeScreenModules.length > 2 ? 2 : 1, [homeScreenModules.length]);
    const heightDivider = useMemo(() => homeScreenModules.length > 1 ? 2 : 1, [homeScreenModules.length]);

    const systemMaintenanceIds = useMemo(() => user && user.systemMaintenanceIds || [], [user]);

    const defaultOpinionView = useMemo(() => user?.userDefaults.opinionPageView === OpinionsView.TABLE ? 'table' : 'map', [user]);

    const homeTilesDisabledForMaintenance = useMemo(() => systemMaintenanceIds.reduce((acc: { homePageLinks: string[], tooltip: string }[], cur) => {
        if (systemMaintenanceOptions[cur] && !isUndefined(systemMaintenanceOptions[cur].homePageLink)) {
            acc = [...acc, { homePageLinks: systemMaintenanceOptions[cur].homePageLink!, tooltip: systemMaintenanceOptions[cur].tooltipMessage }];
        }
        return acc;
    }, []), [systemMaintenanceIds]);

    const tileDisabledTooltip = useCallback((tile: HomePageLink) => {
        const disabledTooltip = homeTilesDisabledForMaintenance.reduce((acc: undefined | string, { homePageLinks, tooltip }) => {
            if (homePageLinks.includes(tile)) {
                acc = tooltip;
            }
            return acc;
        }, undefined);
        return disabledTooltip;
    }, [homeTilesDisabledForMaintenance]);

    useEffect(() => {
        setShowSpinner(true);
        if (screenWidth && screenHeight) {
            const width = ((screenWidth - WRAPPER_MARGIN_X) / widthDivider) - (widthDivider * MODULE_MARGIN_PADDING);
            const height = ((screenHeight - (NAV_BAR + WRAPPER_MARGIN_Y)) / heightDivider) - (heightDivider * MODULE_MARGIN_PADDING);
            setTileDimensions({ height, width });
            setTimeout(() => setShowSpinner(false), 50);
        }
    }, [screenWidth, screenHeight, setShowSpinner, widthDivider, heightDivider]);

    // Added to clear everything related to dataset instance in the store in case a document selected has no dataset linked
    useEffect(() => {
        dispatch(resetDatasetInstance());
        dispatch(resetDocumentInstance());
        dispatch(resetAllOpinions());
        dispatch(resetOpinionInstance());
    }, [dispatch]);

    const getContentByModule = useCallback((module: HomePageModule) => {
        switch (module) {
            case HomePageModule.DOCUMENTS:
                return (
                    <DocumentsModule analyticsSpinner={showSpinner} dimensions={tileDimensions} shouldDoubleWidth={homeScreenModules.length === 3} reduceMapHeight={[1, 4].includes(homeScreenModules.length)} internetGatewayOpen={internetGatewayOpen} tileDisabledTooltip={tileDisabledTooltip} systemMaintenanceIds={systemMaintenanceIds} />
                );
            case HomePageModule.OPINIONS:
                return (
                    <OpinionsModule dimensions={tileDimensions} reduceMapHeight={homeScreenModules.length !== 2} analyticsSpinner={showSpinner} shouldRightAlignTitle={homeScreenModules.length === 4} tileDisabledTooltip={tileDisabledTooltip} defaultOpinionView={defaultOpinionView} systemMaintenanceIds={systemMaintenanceIds} />
                );
            case HomePageModule.ADMIN:
                return (
                    <AdminModule analyticsSpinner={showSpinner} dimensions={tileDimensions} shouldRightAlignTitle={homeScreenModules.length === 3} tileDisabledTooltip={tileDisabledTooltip} systemMaintenanceIds={systemMaintenanceIds} />
                );
            default:
                return null;
        }
    }, [showSpinner, tileDimensions, homeScreenModules.length, internetGatewayOpen, tileDisabledTooltip, defaultOpinionView, systemMaintenanceIds]);

    const fixedOffset = useMemo(() => MODULE_MARGIN + (MODULE_BORDER_WIDTH / zoomRatio), [zoomRatio]);

    const getModuleStyle = useCallback((index: number) => {
        const numberOfModules = homeScreenModules.length;
        const shouldSplitWidth = (numberOfModules === 3 && index !== 0) || numberOfModules === 4;
        const shouldSplitHeight = numberOfModules > 1;
        return { width: `calc(${shouldSplitWidth ? '50%' : '100%'} - ${fixedOffset}px)`, height: `calc(${shouldSplitHeight ? '50%' : '100%'} - ${fixedOffset}px)` };
    }, [homeScreenModules.length, fixedOffset]);

    if (showSpinner) {
        return <PageLoadingDots />;
    }

    return (
        <div className={styles.homePageWrapper}>
            <div className={styles.modulesWrapper}>
                {homeScreenModules.map((module, index) => (
                    <div key={module} className={styles.moduleWrapper} style={getModuleStyle(index)}>
                        {getContentByModule(module)}
                    </div>
                ))}
            </div>
            <DocumentDetailsModal isOpen={documentDetailsModalOpen} />
            <UploadDocumentsModal />
        </div>
    );
};
